import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';

interface ComboboxProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: { id: number; name: string }[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: string | null;
  className?: string;
}

/**
 * Autocomplete combobox — shows filtered suggestions from `options` but always
 * allows free-text entry. Used for vehicle make/model selection where the user
 * may need to type a value not in the curated fleet defaults list.
 */
export function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  loading,
  error,
  className = '',
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = inputValue.trim()
    ? options.filter(o => o.name.toLowerCase().includes(inputValue.toLowerCase()))
    : options;

  // Limit visible items for performance
  const visible = filtered.slice(0, 50);

  function handleSelect(name: string) {
    setInputValue(name);
    onChange(name);
    setOpen(false);
  }

  function handleInputChange(val: string) {
    setInputValue(val);
    onChange(val);
    if (!open) setOpen(true);
  }

  function handleFocus() {
    if (options.length > 0) setOpen(true);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  function handleChevronClick() {
    if (disabled) return;
    if (open) {
      setOpen(false);
    } else {
      setOpen(true);
      inputRef.current?.focus();
    }
  }

  const inputCls =
    'w-full h-11 pl-3 pr-9 rounded-lg border border-stone bg-white text-small text-midnight placeholder:text-slate ' +
    'focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          autoComplete="off"
          value={inputValue}
          onChange={e => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={inputCls}
        />
        {loading ? (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <LoadingSpinner size="sm" />
          </div>
        ) : (
          <button
            type="button"
            tabIndex={-1}
            onClick={handleChevronClick}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate hover:text-midnight transition-colors"
            aria-label="Show options"
          >
            <ChevronDown size={14} className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {error && (
        <p className="text-[11px] text-rose mt-1">{error} — type manually</p>
      )}

      {open && !loading && visible.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-stone bg-white shadow-lg">
          {visible.map(o => (
            <li key={o.id}>
              <button
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSelect(o.name)}
                className={`w-full text-left px-3 py-2 text-small transition-colors duration-100
                  ${o.name === value ? 'bg-plum/10 text-plum font-semibold' : 'text-midnight hover:bg-mist'}`}
              >
                {o.name}
              </button>
            </li>
          ))}
          {filtered.length > 50 && (
            <li className="px-3 py-2 text-[11px] text-slate text-center">
              Type to narrow results ({filtered.length} total)
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
